import { useReduxState } from 'hooks';
import {
  Avatar,
  Badge,
  EmptyIndicator,
  FlexContainer,
  ProgressBar
} from 'components/common';
import { Box } from 'components/common/box';

import type { AchievementDiscussionAnalysisResult } from 'shared-types';
import type { BadgeCategory } from 'components/common/Badge';

interface Props {
  insights: AchievementDiscussionAnalysisResult['businessInsights'];
}

const ConversationAnalysisBusinessInsights = ({ insights }: Props) => {
  const { messages, theme } = useReduxState();

  const businessInsightGroups: {
    [key: string]: { category: BadgeCategory; label: string };
  } = {
    development_area: {
      category: 'warning-light',
      label: messages.achievementConversationAnalysesDevelopmentArea
    },
    other: {
      category: 'dark',
      label: messages.commonOther
    },
    strength: {
      category: 'success-light',
      label: messages.achievementConversationAnalysesStrengts
    }
  };

  return (
    <div className="business-insights">
      <Box
        title={messages.achievementConversationAnalysesBusinessInsights}
        titleDescription={
          messages.achievementConversationAnalysesBusinessInsightsDescription
        }
      >
        {insights?.length ? (
          <div className="insights">
            {insights.map((insight, insightIndex) => (
              <div className="insight" key={insightIndex}>
                <div className="insight-inner">
                  <div className="header">
                    <h4 className="title">{insight.title}</h4>

                    <div className="header-data">
                      <div className="group">
                        <Badge
                          category={
                            businessInsightGroups[insight.group]?.category
                          }
                          text={businessInsightGroups[insight.group]?.label}
                        />
                      </div>

                      <div className="frequency">
                        <ProgressBar
                          color={theme.chartMainColor}
                          label={
                            <FlexContainer
                              style={{
                                gap: 20,
                                justifyContent: 'space-between'
                              }}
                            >
                              <div>
                                {
                                  messages.achievementConversationAnalysesFrequency
                                }
                              </div>
                              <div>{`${insight.prevalence}%`}</div>
                            </FlexContainer>
                          }
                          labelStyle={{
                            color: theme.textColorLighter,
                            marginBottom: 1
                          }}
                          progress={insight.prevalence * 0.01}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="description">{insight.description}</div>

                  <div className="recommended-action">
                    <div className="recommended-action-header">
                      <div className="recommended-action-icon">
                        <Avatar
                          backgroundColor={theme.mainColor}
                          color={theme.mainTextColor}
                          fontSize="0.9rem"
                          icon="arrow-right"
                          size="20px"
                        />
                      </div>

                      {
                        messages.achievementConversationAnalysesRecommendedAction
                      }
                    </div>

                    <div className="recommended-action-content">
                      {insight.recommendedAction}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyIndicator
            icon="briefcase"
            size="small"
            title={messages.achievementConversationAnalysesBusinessInsightsNone}
          />
        )}
      </Box>

      <style jsx>{`
        .insights {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }
        .insight {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
          width: 100%;
        }
        .header {
          align-items: center;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 10px 50px;
          justify-content: space-between;
        }
        .title {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          margin: 0;
        }
        .header-data {
          align-items: center;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 10px 50px;
          justify-content: space-between;
        }
        .description {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          margin-top: 10px;
        }
        .recommended-action {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          margin-top: 20px;
          padding: 15px;
        }
        .recommended-action-header {
          color: ${theme.textColor};
          display: flex;
          font-size: 1.5rem;
          font-weight: 600;
          gap: 10px;
        }
        .recommended-action-icon {
          font-size: 1rem;
        }
        .recommended-action-content {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default ConversationAnalysisBusinessInsights;
